/* eslint-disable no-param-reassign */
import { type LinearGradientObject, type LineSeriesOption } from 'echarts';
import { util } from 'echarts/core';
import { overrideIfUndefined } from '../../../util/data';
import { getRange, isObject } from '../../../util/utils';
import { type ThemeParser } from '../../../core/theme';
import { GridThemeParse } from '../component/grid';
import { ToolTipThemeParse } from '../component/toolTip';
import { xAxisThemeParse } from '../component/xAxis';
import { xAxisPointerThemeParse } from '../component/xAxisPointer';
import { yAxisThemeParse } from '../component/yAxis';
import { getLineColor } from '../static/color';
import { fontColor, FONT_LEVEL_LIST } from '../static/font';
import { Option } from '../../../core/option';
import { legendThemeParse } from '../component/legend';

// TODO color 配置项优先级   用户配置 > 主题配置 > 默认配置
const DEFAULT_DENSITY = 13;
export const MAIN_LINE_WIDTH = 1.5;
export const LINE_WIDTH = 1;
const DEFAULT_DIAM = 4;

/**
 * 折线高低密度标记
 */
function parseLineSymbol(seriesOption: LineSeriesOption[]) {
  seriesOption.forEach(option => {
    const dataLen = option.data.length;
    const isLowDensity = dataLen <= DEFAULT_DENSITY;
    const symbolSize = DEFAULT_DIAM;
    option.symbol = overrideIfUndefined(option.symbol, 'emptyCircle');
    option.dvShowSymbolStrategy = overrideIfUndefined(option.dvShowSymbolStrategy, DEFAULT_DENSITY);
    option.symbolSize = overrideIfUndefined(option.symbolSize, symbolSize);
  });
}
/**
 * 折线最大值最小值的label标签
 * 这里每次最大最小的标签筛选都遍历了每一条折线下的每一个数据（n*n），风险很大，待优化
 *
 */
function parseLineLabelFormatter(
  seriesOption: LineSeriesOption[],
  ...args: Parameters<ThemeParser>
) {
  const [, , optionToken] = args;

  seriesOption.forEach((option, index) => {
    const range = getRange(option.data as number[]);
    option.labelLayout = {
      moveOverlap: 'shiftY'
    };
    const labelOption = {
      show: true,
      formatter: params => {
        if (params.data === range[0] || params.data === range[1]) {
          return String(params.data);
        }
        return '';
      },
      color: getLineColor(seriesOption.length, index, optionToken),
      fontSize: optionToken.fontSizeList.seriesLabel,
      fontFamily: optionToken.fontFamilyList.thsFontFamilyMedium
    };
    option.label = util.merge(option.label || {}, labelOption);
  });
}

/**
 * 设置折线图series的折线线段颜色，默认值为LINE_SERIES_COLOR
 * @todo 这里只设置了series.lineStyle.color的颜色，拐点标志样式还没有同步设置
 * @param seriesOption
 */
function parseLineColor(seriesOption: LineSeriesOption[], ...args: Parameters<ThemeParser>) {
  const [, , optionToken] = args;

  seriesOption.forEach((series, index) => {
    const seriesColor = getLineColor(seriesOption.length, index, optionToken);
    const themeItemStyle = {
      color: seriesColor
    };
    const seriesItemStyle = util.merge({}, series.itemStyle);
    series.itemStyle = util.merge(seriesItemStyle, themeItemStyle);
  });
}

/**
 * 设置折线图series的折线线宽和圆角，默认值为LINE_SERIES_COLOR
 * @default echarts的折线绘制的默认平滑度满足标准 不再设置
 * @param seriesOption
 */
function parseLineStyle(seriesOption: LineSeriesOption[]) {
  const singleLineWidth = 1.5;
  const multiLineWidth = 1;
  let curLineWidth = seriesOption.length > 1 ? multiLineWidth : singleLineWidth;
  seriesOption.forEach(series => {
    if (series.id === 'mainLine') {
      curLineWidth = singleLineWidth;
    } else {
      curLineWidth = multiLineWidth;
    }
    series.lineStyle = util.merge({ width: curLineWidth }, series.lineStyle || {}, true);
  });
}

/**
 * 折线高密度去点逻辑
 * @param option
 */
function parseSmoothMonotone(seriesOption: LineSeriesOption[]) {
  seriesOption.forEach(series => {
    series.smoothMonotone = overrideIfUndefined(series.smoothMonotone, 'x');
    // 高密度过滤数据
    series.sampling = overrideIfUndefined(series.sampling, 'lttb');
    series.clip = false;
  });
}

/**
 * 配置主题中主线的渐变面积
 * @default 主题默认只有id设置了mainLine的折线的第一个series才有渐变面积
 * @param seriesOption
 */
function parseLineArea(seriesOption: LineSeriesOption[]) {
  const mainLines = seriesOption.filter(series => series.id === 'mainLine');
  const mainLine = mainLines[0] || undefined;
  const areaStyle = {
    origin: 'start',
    color: {
      type: 'linear',
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        {
          offset: 1,
          color: 'rgba(51,102,255,0)'
        },
        {
          offset: 0,
          color: 'rgba(51,102,255,0.18)'
        }
      ]
    }
  };
  if (isObject(mainLine)) {
    mainLine.areaStyle = overrideIfUndefined(mainLine.areaStyle, {});
    mainLine.areaStyle.color = overrideIfUndefined(
      mainLine.areaStyle.color,
      areaStyle.color as LinearGradientObject
    );
    mainLine.areaStyle.origin = overrideIfUndefined(mainLine.areaStyle.origin, areaStyle.origin);
  }
}

// 范式-主线symbol放大倍数
const MAIN_SYMBOL_SCALE = 1.2;

/**
 * 配置折线的emphasis的scale
 */
function parseLineEmphasis(seriesOption: LineSeriesOption[]) {
  seriesOption.forEach(series => {
    series.emphasis = overrideIfUndefined(series.emphasis, {});
    series.emphasis.scale = overrideIfUndefined(
      series.emphasis.scale,
      series.id === 'mainLine' ? MAIN_SYMBOL_SCALE : false
    );
  });
}

function judgeIsNormalLine(option: Option) {
  if (typeof option?.series === 'undefined') {
    return false;
  }

  const lineSeries = option.series.filter(item => item.type === 'line');
  return !(lineSeries.length === 0);
}

/**
 * 移动端主题-折线图解析器
 */
export function lineThemeParse(...args: Parameters<ThemeParser>) {
  const [option] = args;
  if (!judgeIsNormalLine(option)) {
    return;
  }
  const lineSeries = option.series.filter(item => item.type === 'line');
  // @ts-ignore
  parseSmoothMonotone(lineSeries);
  parseLineSymbol(lineSeries);
  parseLineColor(lineSeries, ...args);
  parseLineArea(lineSeries);
  parseLineLabelFormatter(lineSeries, ...args);
  parseLineStyle(lineSeries);
  parseLineEmphasis(lineSeries);

  // component按图表需要引入
  GridThemeParse(...args);
  xAxisPointerThemeParse(...args);
  ToolTipThemeParse(...args);

  legendThemeParse(...args);
  xAxisThemeParse(...args);
  yAxisThemeParse(...args);
}
